import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Input, input, output, OnDestroy, OnInit, inject } from '@angular/core';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CropSoilAnalysis, GetSoilCropAnalysesResponse } from 'src/app/bem/shared/models/models';
import { Advice } from 'src/app/tga/shared/constants';
import { MatButton } from '@angular/material/button';
import { NutrientLabelColumnComponent } from './components/nutrient-label-column/nutrient-label-column.component';
import { CropSoilAnalysisColumnComponent } from './components/crop-soil-analysis-column/crop-soil-analysis-column.component';
import { ValuationBarColumnComponent } from './components/valuation-bar-column/valuation-bar-column.component';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { LabelColumnComponent } from './components/label-column/label-column.component';
import { ImageColumnComponent } from './components/image-column/image-column.component';
import { EmptyWidgetComponent } from '../../../../shared/components/empty-widget.component';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'app-crop-soil-analysis-block',
    templateUrl: './crop-soil-analysis-block.component.html',
    styleUrl: './crop-soil-analysis-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButton,
        NutrientLabelColumnComponent,
        CropSoilAnalysisColumnComponent,
        ValuationBarColumnComponent,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        LabelColumnComponent,
        ImageColumnComponent,
        EmptyWidgetComponent,
        NotificationEmptyComponent,
        MatProgressSpinner,
        AsyncPipe,
        DatePipe,
        TranslatePipe,
    ],
})
export class CropSoilAnalysisBlockComponent implements OnInit, OnDestroy {
  private readonly localeService = inject(LocaleService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  @Input() cropSoilAnalysisData$!: Observable<GetSoilCropAnalysesResponse | null>;
  displayMode = input<'bem' | 'tga'>('bem');
  isParticipatingInBem = input<boolean>(true);
  isSeedpotato = input<boolean>(false);
  adviceButtonClick = output<void>();
  readonly destroy$ = new Subject<void>();

  topImg = '../../../assets/images/lab-result-plant.jpg';
  bottomImg = '../../../assets/images/lab-result-soil.jpg';
  data: (CropSoilAnalysis | undefined)[] = [];
  locale!: string;
  isMobile = false;

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  get latestCropSoilAnalysis(): CropSoilAnalysis | null {
    return this.data[2] ?? this.data[1] ?? this.data[0] ?? null;
  }

  get showSoilNitrate(): boolean {
    return !!this.data[2] || this.isSeedpotato();
  }

  ngOnInit(): void {
    this.subscribeToSoilCropAnalysisData();
    this.checkIsMobile();
  }

  checkIsMobile(): void {
    this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
  }

  isSuccessResponse(response: GetSoilCropAnalysesResponse): response is CropSoilAnalysis[] {
    return response != null && Array.isArray(response) && response.length > 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToSoilCropAnalysisData(): void {
    const sampleNumbers: CropSoilAnalysis['sampleNumber'][] = ['One', 'Two', 'Three'];
    this.cropSoilAnalysisData$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      // Keep missing moments empty instead of moving later analyses into their columns.
      const analyses = response && this.isSuccessResponse(response)
        ? response.filter((analysis) =>
          analysis.sampleNumber !== 'Three' ||
          (analysis.soilAnalysisDate != null && analysis.plantAnalysisDate != null))
        : [];
      this.data = sampleNumbers.map((sampleNumber) =>
        analyses.find((analysis) => analysis.sampleNumber === sampleNumber && !!analysis.sampleDate),
      );
    });
  }

  onAdviceButtonClick(): void {
    this.adviceButtonClick.emit();
    this.ai.trackEvent(Advice.cultivationAdiceShowBEMadviceTrends, {})
  }

}
